/**
 * NSLDashboard.jsx — Super Admin Shell · Non-Stop Learning Platform
 *
 * Responsabilidades de este archivo:
 *   ✅ Dual-theme tokens (useT)
 *   ✅ Responsive breakpoints (useBreakpoint)
 *   ✅ Sidebar (desktop fija · mobile slide-in)
 *   ✅ Topbar (role selector · theme toggle · avatar)
 *   ✅ Router → renderContent() despacha a cada tab
 *   ✅ Importa estilos globales
 *
 * Las pestañas reales viven en sus propios módulos:
 *   📦 OverviewTab, FinancialTriageTab, CourseCmsTab,
 *      MiniGamesCmsTab, GlossaryCmsTab
 */

import { useState, useEffect } from 'react';
import './NSLDashboard.css';

import {
  LayoutDashboard,
  CreditCard,
  BookOpen,
  Gamepad2,
  BookMarked,
  Sun,
  Moon,
  ChevronDown,
  Menu,
  X,
  Zap,
  Music,
  Video,
  Library,
  Film,
  GraduationCap,
  Handshake,
  Bot,
  MessageSquare,
  Target
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────
   MOCK TABS
   En producción, reemplaza estos imports por los reales:

   import OverviewTab       from './tabs/OverviewTab';
   import FinancialTriageTab from './tabs/FinancialTriageTab';
   import CourseCmsTab      from './tabs/CourseCmsTab';
   import MiniGamesCmsTab   from './tabs/MiniGamesCmsTab';
   import GlossaryCmsTab    from './tabs/GlossaryCmsTab';
───────────────────────────────────────────────────────── */

import CourseCmsTab from './tabs/CourseCmsTab';
import MiniGamesCmsTab from './tabs/minigames_cms/MiniGamesCmsTab';
import GlossaryCmsTab from './tabs/glossary_cms/GlossaryCmsTab';
import OverviewTab from './tabs/overview_cms/OverviewTab';
import FinancialDashboard from './tabs/financial_hub/Financialdashboard';
import SyncEditorTab from './tabs/SyncEditorTab';
import EduEditorTab from './tabs/EduEditorTab';
import SongLibraryTab from './tabs/SongLibraryTab';
import VideoLibraryTab from './tabs/VideoLibraryTab';
import ExamsCmsTab from './tabs/exams_cms/ExamsCmsTab';
import AsistenteCmsTab from './tabs/asistente_cms/AsistenteCmsTab';
import SpacyCoachTab from './tabs/SpacyCoachTab';
import AfiliadosTab from './tabs/AfiliadosTab';
import LeadsTab from './tabs/LeadsTab';


/* ── EL CARTEL QUE DECIA ESTAR EN PRODUCCION ────────────────────────────
   Aqui vivia `makeMockTab`, que pintaba un placeholder con esta frase:

       «Componente {label} montado en producción 🚀»

   Lo usaba una sola pestaña —Glossary CMS— y no estaba montada: era un
   cartel. Un placeholder que ANUNCIA estar en produccion es peor que uno
   que dice «pendiente», porque nadie vuelve a mirarlo.

   Retirado el 30-08-2026, cuando la pestaña se hizo de verdad. Si algun dia
   hace falta un hueco para una pestaña por escribir, que diga lo que es.
   ──────────────────────────────────────────────────────────────────── */

// Glossary CMS ya no es un cartel: la pestaña existe desde el 30-08-2026.
// Era la unica de las diez del Command Center que era de mentira, mientras el
// glosario —13.546 palabras, el contenido mas grande de NSL— solo se podia
// tocar entrando a la base a mano.

/* ─────────────────────────────────────────────────────────
   NAVIGATION TABS
───────────────────────────────────────────────────────── */
const TABS = [
  { id: 'Overview', label: 'Overview', icon: LayoutDashboard, component: OverviewTab },
  { id: 'Financial & CRM', label: 'Financial & CRM', icon: CreditCard, component: FinancialDashboard },
  { id: 'Course CMS', label: 'Course CMS', icon: BookOpen, component: CourseCmsTab },
  { id: 'Mini Games CMS', label: 'Mini Games CMS', icon: Gamepad2, component: MiniGamesCmsTab },
  { id: 'Glossary CMS', label: 'Glossary CMS', icon: BookMarked, component: GlossaryCmsTab },
  { id: 'Exams CMS', label: '🎓 Examen de Nivel', icon: GraduationCap, component: ExamsCmsTab },
  { id: 'Asistente CMS', label: '🤖 Asistente Spacy', icon: Bot, component: AsistenteCmsTab },
  // El coach habla a solas con cada alumno: sin esta pestana, la unica forma
  // de saber si lo hace bien es que alguien se queje.
  { id: 'Spacy Coach', label: '🏋️ Spacy Coach', icon: MessageSquare, component: SpacyCoachTab },

  // La mesa de control del negocio de afiliados. NO va dentro de «Financial &
  // CRM» a proposito: alli el profesor cobra por CLASES DADAS —nomina, tarifa
  // por clase— y aqui cobra un PORCENTAJE de lo que pagan los alumnos que
  // trajo. Son dos economias sobre las mismas personas y sus saldos no son
  // intercambiables; el dia que las dos cifras aparezcan en la misma tarjeta,
  // alguien va a pagar una creyendo que paga la otra.
  { id: 'Afiliados', label: '🤝 Afiliados', icon: Handshake, component: AfiliadosTab },

  // Quien hizo el diagnostico de la portada y dejo su contacto sin crear
  // cuenta. Hasta el 24-09-2026 esa gente se guardaba en `onboarding_leads` y
  // NO se podia ver desde ningun sitio: habia que entrar a la base a mano.
  //
  // No va dentro de «Financial & CRM» a proposito: alli viven los alumnos, que
  // son gente con cuenta, plan y dinero de por medio. Aqui no hay cuenta ni
  // pago todavia, y mezclarlos haria que un lead pareciera un cliente.
  { id: 'Leads', label: '🎯 Leads', icon: Target, component: LeadsTab },

  // -- SECCIÓN MÚSICA --
  { id: 'Sync Editor', label: 'Sync Editor', icon: Music, component: SyncEditorTab },
  { id: 'Song Library', label: '📚 Canciones', icon: Library, component: SongLibraryTab },

  // -- SECCIÓN DOCUMENTALES --
  { id: 'Video Edu', label: '📹 Video Edu', icon: Video, component: EduEditorTab },
  { id: 'Video Library', label: '🎬 Videos Edu', icon: Film, component: VideoLibraryTab },
];

const ROLES = ['Super Admin', 'Content Editor', 'Analyst'];

/* ─────────────────────────────────────────────────────────
   DESIGN TOKEN HOOK
   dark  → Matte Black Neumorphism + Neon Glass
   light → Off-white Neumorphism
───────────────────────────────────────────────────────── */
function useT(dark) {
  /* ── Backgrounds ── */
  const bg = dark ? '#1a1a1a' : '#e0e5ec';   // ← negro matte, no morado
  const sd = dark ? '#0a0a0a' : '#a3b1c6';   // shadow dark
  const sl = dark ? '#2a2a2a' : '#ffffff';   // shadow light

  /* ── Text ── */
  const tx = dark ? '#e8e8f0' : '#1a1a2e';
  const tx2 = dark ? '#7a7a9a' : '#7a8499';

  /* ── Accents ── */
  const ac = dark ? '#00FFFF' : '#ffff00';   // cyan  / yellow
  const ok = dark ? '#00ff88' : '#00cc66';
  const er = dark ? '#ff6b6b' : '#f87171';
  const wrn = dark ? '#f59e0b' : '#d97706';
  const blu = '#1d4ed8';

  /* ── Neumorphic shadows ── */
  const nxl = `-12px 12px 24px ${sd}, 12px -12px 24px ${sl}`;
  const nlg = `-10px 10px 20px ${sd}, 10px -10px 20px ${sl}`;
  const nmd = `-6px 6px 12px ${sd}, 6px -6px 12px ${sl}`;
  const nsm = `-3px 3px 8px ${sd}, 3px -3px 8px ${sl}`;
  const nxs = `-2px 2px 5px ${sd}, 2px -2px 5px ${sl}`;
  const ism = `inset -3px 3px 8px ${sd}, inset 3px -3px 8px ${sl}`;
  const ixs = `inset -2px 2px 5px ${sd}, inset 2px -2px 5px ${sl}`;

  return {
    dark, bg, sd, sl, tx, tx2, ac, ok, er, wrn, blu,
    nxl, nlg, nmd, nsm, nxs, ism, ixs,
  };
}

/* ─────────────────────────────────────────────────────────
   RESPONSIVE BREAKPOINT HOOK
───────────────────────────────────────────────────────── */
function useBreakpoint() {
  const [w, setW] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );

  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  return {
    w,
    isMobile: w < 640,
    isTablet: w >= 640 && w < 1024,
    isDesktop: w >= 1024,
  };
}

/* ═══════════════════════════════════════════════════════════
   MAIN DASHBOARD SHELL
═══════════════════════════════════════════════════════════ */
export default function NSLDashboard() {
  const [dark, setDark] = useState(true);
  const [role, setRole] = useState('Super Admin');
  const [tab, setTab] = useState('Overview');
  const [roleOpen, setRoleOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);

  const t = useT(dark);
  const bp = useBreakpoint();
  const HICOLOR = dark ? t.ac : t.blu;
  const { isMobile } = bp;

  /* ── Close dropdowns on outside click ── */
  useEffect(() => {
    const fn = (e) => {
      if (roleOpen && !e.target.closest('[data-rzone]')) setRoleOpen(false);
      if (sideOpen && !e.target.closest('[data-sidebar]')
        && !e.target.closest('[data-hamburger]')) setSideOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [roleOpen, sideOpen]);

  const handleTabChange = (id) => {
    setTab(id);
    if (isMobile) setSideOpen(false);
  };

  /* ── PASARLE UN TRABAJO A OTRA PESTAÑA (06-10-2026) ──────────────────────
     Rob: «lo ideal es un botón que pueda editar todo el ejercicio ya creado,
     digamos que doy clic y me lleva a Edu Video Editor, pero con toda la data».

     Las pestañas no se conocen entre ellas y así tiene que seguir siendo. Lo
     que se añade es un buzón: quien abre deja aquí lo que manda, el dashboard
     cambia de pestaña, y la que recibe lo coge UNA vez y lo vacía.

     Vaciarlo importa: sin eso, salir de la pestaña y volver recargaría el mismo
     vídeo encima de lo que se estuviera corrigiendo. */
  const [encargo, setEncargo] = useState(null);
  const abrirEn = (idDePestana, carga) => {
    setEncargo({ para: idDePestana, carga });
    handleTabChange(idDePestana);
  };

  /* ── Router ── */
  const renderContent = () => {
    const found = TABS.find((t) => t.id === tab);
    if (!found) return null;
    const TabComponent = found.component;
    return (
      <TabComponent
        t={t} HICOLOR={HICOLOR} dark={dark} bp={bp}
        abrirEn={abrirEn}
        encargo={encargo?.para === found.id ? encargo.carga : null}
        tomaEncargo={() => setEncargo(null)}
      />
    );
  };

  /* ── Shared sidebar content (used in desktop aside + mobile overlay) ── */
  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '4px 6px 16px', marginBottom: 8,
        borderBottom: `1px solid ${dark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.07)'}`,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 900, background: t.bg,
          boxShadow: `${t.nmd}, 0 0 0 1.5px ${HICOLOR}33`,
          color: HICOLOR,
          textShadow: dark ? `0 0 14px ${t.ac}` : '',
        }}>N</div>
        <div>
          <div style={{
            fontSize: 15, fontWeight: 900, letterSpacing: '-.03em',
            color: t.tx, lineHeight: 1
          }}>NSL</div>
          <div style={{
            fontSize: 9, color: t.tx2, letterSpacing: '.14em',
            textTransform: 'uppercase', marginTop: 2
          }}>Platform</div>
        </div>
        {/* Mobile close button */}
        {isMobile && (
          <button
            onClick={() => setSideOpen(false)}
            style={{
              marginLeft: 'auto', background: 'transparent', border: 'none',
              cursor: 'pointer', color: t.tx2, display: 'flex', alignItems: 'center'
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
        {TABS.map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          return (
            <NavItem
              key={id}
              id={id}
              label={label}
              Icon={Icon}
              active={active}
              t={t}
              HICOLOR={HICOLOR}
              dark={dark}
              onClick={() => handleTabChange(id)}
            />
          );
        })}
      </nav>

      {/* SpaCy live badge */}
      <div style={{
        padding: '11px 12px', borderRadius: 14, marginTop: 8,
        background: t.bg,
        boxShadow: `${t.ixs}, 0 0 0 1.5px ${t.ok}28`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', marginBottom: 4
        }}>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '.14em',
            textTransform: 'uppercase', color: t.ok,
            textShadow: dark ? `0 0 10px ${t.ok}` : '',
          }}>SpaCy IA</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              className="nsl-live-dot"
              style={{
                width: 6, height: 6, borderRadius: '50%', background: t.ok,
                boxShadow: dark ? `0 0 8px ${t.ok}` : '',
              }}
            />
            <span style={{
              fontSize: 10, fontWeight: 700, color: t.ok,
              textShadow: dark ? `0 0 8px ${t.ok}77` : ''
            }}>Live</span>
          </div>
        </div>
        <div style={{ fontSize: 11, color: t.tx2, marginBottom: 5 }}>v3.7 · 94.2% accuracy</div>
        <div style={{ height: 4, borderRadius: 2, overflow: 'hidden', boxShadow: t.ixs }}>
          <div style={{
            width: '94.2%', height: '100%', borderRadius: 2, background: t.ok,
            boxShadow: dark ? `0 0 8px ${t.ok}88` : '',
          }} />
        </div>
      </div>
    </>
  );

  /* ── Root layout ── */
  return (
    <div className="nsl-dashboard-viewport">
      <div style={{
        display: 'flex', width: '100%', height: '100%',
        background: t.bg, color: t.tx,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        transition: 'background .3s, color .3s',
      }}>

      {/* ── Desktop / Tablet sidebar ── */}
      {!isMobile && (
        <aside
          data-sidebar
          style={{
            width: 214, minWidth: 214, display: 'flex', flexDirection: 'column',
            padding: '18px 10px', background: t.bg,
            boxShadow: `4px 0 16px ${t.sd}88`,
            borderRight: `1px solid ${dark ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.05)'}`,
            zIndex: 10, transition: 'all .3s',
          }}
        >
          <SidebarContent />
        </aside>
      )}

      {/* ── Mobile sidebar overlay ── */}
      {isMobile && sideOpen && (
        <div
          className="nsl-sidebar-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setSideOpen(false); }}
        >
          <aside
            data-sidebar
            className="nsl-slide-in"
            style={{
              position: 'absolute', top: 0, left: 0, bottom: 0, width: 220,
              display: 'flex', flexDirection: 'column',
              padding: '18px 10px', background: t.bg,
              boxShadow: `8px 0 32px ${t.sd}`,
            }}
          >
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* ── Main area ── */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', minWidth: 0
      }}>

        {/* ── Topbar (Tech-Noir Sticky Glass) ── */}
        <header 
          className="nsl-glass-header"
          style={{
            position: 'sticky',
            top: 0,
            height: isMobile ? 64 : 56,
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `0 ${isMobile ? '16px' : '22px'}`,
            flexShrink: 0, 
            background: dark ? 'rgba(26, 26, 26, 0.75)' : 'rgba(224, 229, 236, 0.75)',
            boxShadow: `0 4px 24px ${t.sd}55`,
            borderBottom: `1px solid ${dark ? 'rgba(255,255,255,.08)' : 'rgba(255,255,255,.6)'}`,
            transition: 'all .3s', 
            zIndex: 50,
        }}>

          {/* Left: hamburger + page title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {isMobile && (
              <button
                data-hamburger
                onClick={() => setSideOpen(!sideOpen)}
                style={{
                  width: 34, height: 34, borderRadius: 9, border: 'none',
                  background: t.bg, boxShadow: t.nsm, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: t.tx, transition: 'all .2s',
                }}
              >
                <Menu size={15} />
              </button>
            )}
            <div>
              <h1 style={{
                fontSize: isMobile ? 14 : 16, fontWeight: 700, color: t.tx,
                letterSpacing: '-.025em', lineHeight: 1
              }}>
                {TABS.find((t) => t.id === tab)?.label ?? tab}
              </h1>
              {!isMobile && (
                <p style={{ fontSize: 11, color: t.tx2, marginTop: 2 }}>
                  {new Date().toLocaleDateString('es-MX', {
                    weekday: 'long', year: 'numeric', month: 'short', day: 'numeric',
                  })}
                </p>
              )}
            </div>
          </div>

          {/* Right: role dropdown + theme + avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

            {/* Role dropdown */}
            <div style={{ position: 'relative' }} data-rzone>
              <button
                onClick={() => setRoleOpen(!roleOpen)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: isMobile ? '6px 8px' : '7px 12px',
                  borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: t.bg, boxShadow: t.nsm, color: t.tx,
                  fontSize: 12, fontWeight: 600, fontFamily: 'Inter, sans-serif',
                  transition: 'all .2s',
                }}
              >
                <div style={{
                  width: 7, height: 7, borderRadius: '50%', background: HICOLOR,
                  boxShadow: dark ? `0 0 6px ${t.ac}` : '',
                }} />
                {!isMobile && role}
                <ChevronDown
                  size={11}
                  style={{
                    opacity: .6, transition: 'transform .2s',
                    transform: roleOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              </button>

              {roleOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                  background: t.bg, borderRadius: 12, padding: 6, zIndex: 999,
                  minWidth: 162, boxShadow: t.nlg,
                  border: `1px solid ${dark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)'}`,
                  animation: 'fadeUp .18s ease',
                }}>
                  {ROLES.map((r) => {
                    const active = role === r;
                    return (
                      <button
                        key={r}
                        onClick={() => { setRole(r); setRoleOpen(false); }}
                        style={{
                          width: '100%', textAlign: 'left', padding: '8px 10px',
                          borderRadius: 8, border: 'none', cursor: 'pointer',
                          fontFamily: 'Inter, sans-serif', fontSize: 12,
                          fontWeight: active ? 700 : 500, background: t.bg,
                          boxShadow: active
                            ? `${t.ism}, 0 0 0 1.5px ${HICOLOR}44`
                            : 'none',
                          color: active ? HICOLOR : t.tx2,
                          textShadow: active && dark ? `0 0 10px ${t.ac}77` : '',
                          transition: 'all .15s',
                        }}
                      >{r}</button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Theme toggle */}
            <button
              onClick={() => setDark(!dark)}
              style={{
                width: 34, height: 34, borderRadius: 10, border: 'none',
                background: t.bg, boxShadow: t.nsm, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: dark ? '#fbbf24' : '#64748b', transition: 'all .2s',
              }}
            >
              {dark ? <Sun size={13} /> : <Moon size={13} />}
            </button>

            {/* Avatar */}
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 900, letterSpacing: '.06em',
              background: t.bg,
              boxShadow: `${t.nsm}, 0 0 0 1.5px ${HICOLOR}33`,
              color: HICOLOR,
              textShadow: dark ? `0 0 12px ${t.ac}` : '',
            }}>SA</div>
          </div>
        </header>

        {/* ── Page content (Smart Edge-to-Edge) ── */}
        <main style={{
          flex: 1, 
          display: 'flex',
          flexDirection: 'column',
          overflowY: tab === 'Financial & CRM' ? 'hidden' : 'auto',
          overflowX: 'hidden',
          padding: tab === 'Financial & CRM' 
            ? '0px' 
            : (isMobile ? '16px 0px 80px 0px' : '18px 20px'),
        }}>
          {renderContent()}
        </main>
      </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   NAV ITEM — extracted to avoid hooks-in-loop issue
───────────────────────────────────────────────────────── */
function NavItem({ id, label, Icon, active, t, HICOLOR, dark, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        width: '100%', textAlign: 'left',
        padding: '8px 10px', borderRadius: 12,
        border: 'none', cursor: 'pointer',
        fontFamily: 'Inter, sans-serif',
        fontSize: 13, fontWeight: active ? 700 : 500,
        color: active ? HICOLOR : (hovered ? t.tx : t.tx2),
        background: t.bg,
        boxShadow: active
          ? `${t.ism}, 0 0 0 1.5px ${HICOLOR}44`
          : hovered ? t.nxs : 'none',
        transition: 'all .2s',
        textShadow: active && dark ? `0 0 10px ${t.ac}77` : '',
      }}
    >
      <Icon size={14} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {active && (
        <div style={{
          marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%',
          background: HICOLOR,
          boxShadow: dark ? `0 0 8px ${t.ac}` : 'none',
          flexShrink: 0,
        }} />
      )}
    </button>
  );
}